import { Singleton, contains } from './common';
import type { Client, RequestOptions } from '../generated/client/types.gen';
import { jwt } from './jwt';
import { refresh } from './refresh';
import type { ApiError } from '../errors';

/**
 * Type definition for the intercept function.
 */
export type RequestInterceptor = (request: Request, options: RequestOptions) => Promise<Request>;

/**
 * Options for configuring the interceptor function.
 */
export type RequestInterceptorOptions = {
  token?: Singleton<string>;
  api_key?: Singleton<string>;
  ignored_urls: string[];
  on_refresh_failed?: (err?: ApiError) => void;
};

/**
 * Creates a request interceptor that proactively refreshes the JWT token if it's about to expire
 * and adds the appropriate authentication headers (Bearer or API Key) to outgoing requests.
 *
 * @param options - Configuration for the interceptor, including the token manager.
 * @returns A function that takes a client instance, registers the interceptor, and returns its ID.
 */
export const request =
  (options: RequestInterceptorOptions) =>
  (client: Client): number => {
    const interceptor: RequestInterceptor = async (request, req_options) => {
      const { token, api_key, ignored_urls, on_refresh_failed } = options;
      if (contains(request.url, ignored_urls)) {
        return request;
      }

      const security = req_options.security;

      if (api_key && security?.find(auth => auth.type === 'apiKey')) {
        request.headers.set('API-Key', `${api_key.get()}`);
        return request;
      }

      if (token && jwt.should_refresh(token.get())) {
        await refresh({ token, client, on_refresh_failed });
      }

      if (token && security?.find(auth => auth.type === 'http' && auth.scheme === 'bearer')) {
        request.headers.set('Authorization', `Bearer ${token.get()}`);
      }

      return request;
    };

    return client.interceptors.request.use(interceptor);
  };
